/**
 * Enhanced useChat hook using TanStack AI
 * Provides streaming, real-time status updates, and seamless integration
 * with our existing Framer Motion animations and orchestrator metadata.
 * 
 * @version 3.0.0 - Migrated from Vercel AI SDK to TanStack AI
 */

import { useAuth } from '@/hooks/useAuth';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAISettings } from '@/hooks/useAISettings';
import { useGoogleGeminiConnection } from '@/hooks/useGoogleGeminiConnection';
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useTanstackChat, createBrainSynapticConnection } from "@flowstate/ai/react";
import {
  useCortexRunAwait,
  useCortexStream,
  isGeminiByokFallbackChunk,
  GEMINI_BYOK_FALLBACK_COPY,
  resolveAssistantErrorText,
  formatBrainUserErrorSummary,
  resolveCortexBrainUrl,
  resolveTaskExtractorUrl,
  resolveSummarizeUrl,
} from "@flowstate/ai-client";
import type { BrainAwaitingResponse, CortexRunRow, ReasoningEffort } from "@flowstate/types";
import type { UIMessage } from "@flowstate/ai/react";
import { ExtendedMessage, MessageAttachment, ConversationTodoItem, ConversationTodoList, OrchestratorMeta, SpecialistData, BrainChunk, TaskExtractorResponse } from "../types/chat";
import { ensureChatSession, generateMessageId, saveMessage as saveMessageToDb } from "../utils/chatUtils";
import { useAgentCapabilities } from "./useAgentCapabilities";
import { agentBranding } from "../config/agentBranding";
import { toast } from '@/components/ui/sonner';
import { DEFAULT_AI_SETTINGS, DEFAULT_GEMINI_FLASH_MODEL, resolveGeminiModelId } from '@flowstate/types';
import { logger } from "@/lib/logger";
import { geminiAuthService } from '@/services/integrations/core/gemini-auth.service';
import { useChatContext } from '@/contexts/ChatContext';
import {
  createTimeline,
  reduceBrainChunk,
  latestRun,
  runToOrchestratorMeta,
  type CortexTimeline,
} from "../../cortex/engine/cortexStreamReducer";

// Interface for conversation list items
export interface Conversation {
  id: string;
  title: string;
  created_at: string;
  organization_id?: string;
}

// Map TanStack AI UIMessage to our ExtendedMessage format
const convertUIMessageToExtended = (
  uiMessage: UIMessage,
  orchestratorMeta?: OrchestratorMeta,
  todoList?: ConversationTodoList
): ExtendedMessage => {
  // Extract text from parts — TanStack AI uses `content` on text parts
  const textParts = uiMessage.parts
    .filter(part => part.type === 'text')
    .map(part => 'content' in part ? (part as { type: 'text'; content: string }).content : '')
    .join('');

  return {
    id: uiMessage.id,
    role: uiMessage.role as 'user' | 'assistant',
    content: textParts,
    orchestratorMeta,
    toolActivity: orchestratorMeta?.toolActivity,
    todoList,
    // TanStack AI doesn't have a file part type — attachments handled separately
    attachments: [],
  };
};

/** Default follow-up suggestions for assistant messages. Can be replaced by API-generated suggestions later. */
function getFollowUpSuggestions(_messageText: string): string[] {
  return [
    'Tell me more',
    'Can you give an example?',
    'Simplify that',
    'What are the next steps?',
  ];
}

export const useChatImpl = () => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useCurrentOrganization();
  const currentOrgId = currentOrganization?.id;
  const { currentProject } = useCurrentProject();
  const currentProjectRef = useRef(currentProject);
  useEffect(() => { currentProjectRef.current = currentProject; }, [currentProject]);
  const { settings: userAISettings } = useAISettings();
  const { accessToken: geminiOAuthToken, isConnected: isGeminiOAuthConnected } = useGoogleGeminiConnection();
  const agentContext = useAgentCapabilities();
  const { pendingNotebookHandoff, clearNotebookHandoff } = useChatContext();
  
  // State for conversation management
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(undefined);
  const [conversationList, setConversationList] = useState<Conversation[]>([]);
  const messagesRef = useRef<UIMessage[]>([]);

  const durableStreamTransport =
    typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_CORTEX_DURABLE_STREAM === "1";

  const patchAssistantMessageRef = useRef<(messageId: string, newContent: string) => void>(() => {});

  const handleStreamToken = useCallback((delta: string, accumulated: string) => {
    void delta;
    const msgs = messagesRef.current;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === "assistant") {
        patchAssistantMessageRef.current(msgs[i].id, accumulated);
        return;
      }
    }
  }, []);

  // Electric durable stream — primary token transport when VITE_CORTEX_DURABLE_STREAM=1
  useCortexStream(currentConversationId, {
    enabled: durableStreamTransport,
    onToken: durableStreamTransport ? handleStreamToken : undefined,
  });
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(true);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  
  // Track orchestrator metadata + detected todo lists per message
  const [orchestratorData, setOrchestratorData] = useState<Record<string, OrchestratorMeta & { todoList?: ConversationTodoList; isExtractingTasks?: boolean; followUpQuestions?: string[]; specialistData?: SpecialistData }>>({});

  // Shared stream→timeline reducer — the same fold the workbench uses for
  // live chunks + cortex_run_events rehydration. The drawer keeps binding
  // meta per message id (orchestratorData), but event accumulation now lives
  // in the reducer rather than bespoke ref bookkeeping.
  const [liveTimeline, setLiveTimeline] = useState<CortexTimeline>(() => createTimeline());
  const liveTimelineRef = useRef<CortexTimeline>(liveTimeline);

  // Buffer for orchestrator status events that arrive before the assistant message
  // is created by TanStack AI's lazy StreamProcessor (v0.5+ creates assistant messages
  // only on first TEXT_MESSAGE_CONTENT, not on TEXT_MESSAGE_START).
  const pendingOrchestratorRef = useRef<Partial<OrchestratorMeta> | null>(null);
  
  // Local input state (AI SDK doesn't provide this directly)
  const [input, setInput] = useState<string>('');
  
  // Track pending attachments before conversation is created
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  
  // Context selection state
  const [selectedContextIds, setSelectedContextIds] = useState<string[]>([]);
  const [summarizedContext, setSummarizedContext] = useState<string | undefined>(undefined);
  const [isSummarizing, setIsSummarizing] = useState(false);
  
  // Helper function to detect QuotaExceededError across browsers
  function isQuotaExceededError(err: unknown): boolean {
    return (
      err instanceof DOMException &&
      (err.code === 22 || 
       err.code === 1014 || 
       err.name === 'QuotaExceededError' || 
       err.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    );
  }

  // Model and provider selection state
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('flowstate.chat.selectedModel');
      if (stored) {
        return stored.includes('/') || stored.startsWith('openrouter/') ? stored : resolveGeminiModelId(stored);
      }
      return DEFAULT_GEMINI_FLASH_MODEL;
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, using default model');
      } else {
        logger.warn('[useChat] Failed to read selectedModel from localStorage:', { error: err instanceof Error ? err.message : err });
      }
      return DEFAULT_GEMINI_FLASH_MODEL;
    }
  });
  
  const [selectedProvider, setSelectedProvider] = useState<string>(() => {
    try {
      return localStorage.getItem('flowstate.chat.selectedProvider') ?? DEFAULT_AI_SETTINGS.provider ?? 'gemini';
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, using default provider');
      } else {
        logger.warn('[useChat] Failed to read selectedProvider from localStorage:', { error: err instanceof Error ? err.message : err });
      }
      return 'gemini';
    }
  });

  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(() => {
    try {
      const stored = localStorage.getItem('flowstate.chat.reasoningEffort');
      return stored === 'low' || stored === 'medium' || stored === 'high'
        ? stored
        : 'medium';
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, using default reasoningEffort');
      } else {
        logger.warn('[useChat] Failed to read reasoningEffort from localStorage:', { error: err instanceof Error ? err.message : err });
      }
      return 'medium';
    }
  });

  const [autoExtractTasks, setAutoExtractTasks] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('flowstate.chat.autoExtractTasks');
      return stored ? Boolean(JSON.parse(stored)) : true;
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, using default autoExtractTasks');
      } else {
        logger.warn('[useChat] Failed to read autoExtractTasks from localStorage:', { error: err instanceof Error ? err.message : err });
      }
      return true;
    }
  });

  const [autoMode, setAutoMode] = useState<boolean>(true);

  // Persist provider/model selection so it survives reloads
  useEffect(() => {
    try { 
      localStorage.setItem('flowstate.chat.selectedModel', selectedModel); 
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, cannot persist selectedModel');
      }
    }
  }, [selectedModel]);
  
  useEffect(() => {
    try { 
      localStorage.setItem('flowstate.chat.selectedProvider', selectedProvider); 
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, cannot persist selectedProvider');
      }
    }
  }, [selectedProvider]);

  useEffect(() => {
    try {
      localStorage.setItem('flowstate.chat.reasoningEffort', reasoningEffort);
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, cannot persist reasoningEffort');
      }
    }
  }, [reasoningEffort]);

  // Selected specialist for direct routing
  const [selectedSpecialist, setSelectedSpecialist] = useState<string | undefined>(undefined);

  // Notebook grounding — when set, Brain uses Gemini File Search for grounded answers
  const [selectedNotebookId, setSelectedNotebookId] = useState<string | undefined>(undefined);

  // Persist task extraction preference
  useEffect(() => {
    try {
      localStorage.setItem('flowstate.chat.autoExtractTasks', String(autoExtractTasks));
    } catch (err) {
      if (isQuotaExceededError(err)) {
        logger.warn('[useChat] localStorage quota exceeded, cannot persist autoExtractTasks');
      }
    }
  }, [autoExtractTasks]);
  
  // Booking suggestion state
  const [showBookingSuggestion, setShowBookingSuggestion] = useState<boolean>(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Ref for pending messages to load after conversation switch
  const pendingMessagesRef = useRef<UIMessage[] | null>(null);

  // Persist current conversation ID per organization
  useEffect(() => {
    if (currentOrgId && currentConversationId) {
      localStorage.setItem(`last-conversation-${currentOrgId}`, currentConversationId);
    }
  }, [currentOrgId, currentConversationId]);

  // Edge function URLs — resolved once from env vars (stable across renders)
  const BRAIN_URL = resolveCortexBrainUrl();
  const TASK_EXTRACTOR_URL = resolveTaskExtractorUrl();
  const SUMMARIZE_URL = resolveSummarizeUrl();

  /**
   * Call the task-extractor edge function to AI-extract action items from a message.
   * @param messageText - The text content of the assistant message
   * @param messageId - The TanStack AI message ID (used for orchestratorData client state)
   * @param dbMessageId - The DB-generated row ID (used for metadata persistence)
   */
  const extractTasksFromMessage = useCallback(async (
    messageText: string,
    messageId: string,
    dbMessageId?: string
  ) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {return;}

      logger.info('🔍 Calling task-extractor for message:', { messageId, dbMessageId });

      // Set extracting state so UI can show loading animation
      setOrchestratorData(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          isExtractingTasks: true,
        },
      }));

      const response = await fetch(TASK_EXTRACTOR_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageContent: messageText,
          messageId: messageId,
          conversationId: currentConversationId,
          ...(isGeminiOAuthConnectedRef.current && userAISettingsRef.current.useGoogleGemini !== false && geminiOAuthTokenRef.current
            ? { geminiAccessToken: geminiOAuthTokenRef.current }
            : {}),
        }),
      });

      if (!response.ok) {
        logger.warn('Task extractor returned non-OK:', { status: response.status });
        return;
      }

      const result = await response.json() as TaskExtractorResponse;

      if (result.success && result.todoList && result.todoList.items.length > 0) {
        logger.info('📋 AI extracted', { count: result.todoList.items.length, type: 'action items' });

        // Update orchestrator data with the extracted todoList
        setOrchestratorData(prev => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            todoList: result.todoList as ConversationTodoList,
          },
        }));

        // Persist todoList to chat_messages.metadata JSONB column (uses DB row ID)
        const persistId = dbMessageId ?? messageId;
        if (currentConversationId && persistId) {
          void persistId;
        }

        toast.success(`${result.todoList.items.length} action item(s) extracted`, {
          description: 'Review them in the Action Items panel',
          duration: 3000,
        });
      } else {
        logger.info('🔍 No actionable items found in message');
      }
    } catch (error: unknown) {
      logger.warn('Task extraction failed (non-critical):', { error });
    } finally {
      // Clear extracting state regardless of success/failure
      setOrchestratorData(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          isExtractingTasks: false,
        },
      }));
    }
  }, [currentConversationId, TASK_EXTRACTOR_URL]);

  // Get auth token for API calls
  const getAuthToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token;
  };

  // ── Refs for dynamic values used in the connection callback ──
  // These avoid recreating the connection on every render while still
  // providing up-to-date values when the request is made.
  const userRef = useRef(user);
  const userAISettingsRef = useRef(userAISettings);
  const geminiOAuthTokenRef = useRef(geminiOAuthToken);
  const isGeminiOAuthConnectedRef = useRef(isGeminiOAuthConnected);
  const selectedModelRef = useRef(selectedModel);
  const selectedProviderRef = useRef(selectedProvider);
  const reasoningEffortRef = useRef(reasoningEffort);
  const autoModeRef = useRef(autoMode);
  const agentContextRef = useRef(agentContext);
  const summarizedContextRef = useRef(summarizedContext);
  const orchestratorDataRef = useRef(orchestratorData);
  const selectedNotebookIdRef = useRef(selectedNotebookId);
  // Per-message extras (attachments, context IDs, specialist) set before each send
  const perMessageDataRef = useRef<Record<string, unknown>>({});
  const currentConversationIdRef = useRef(currentConversationId);
  const currentOrgIdRef = useRef(currentOrgId);
  const setCurrentConversationIdRef = useRef(setCurrentConversationId);
  const [synapticRunId, setSynapticRunId] = useState<string | undefined>();
  const [synapticSessionId, setSynapticSessionId] = useState<string | undefined>();
  const synapticAwaitingRef = useRef<BrainAwaitingResponse | null>(null);
  // ask_user human-in-the-loop — set when the brain 202s with awaiting_input.
  const [pendingQuestion, setPendingQuestion] = useState<{
    runId: string;
    question: string;
    options?: string[];
    allowFreeText?: boolean;
  } | null>(null);
  const byokNoticeShownRef = useRef(false);
  const lastBrainErrorRef = useRef<string | null>(null);
  // cortex_runs id of the in-flight foreground run (from RUN_STARTED), used to
  // recover response_output if the SSE transport drops mid-run.
  const activeCortexRunRef = useRef<string | null>(null);
  const intentionalStopRef = useRef(false);

  useEffect(() => { currentConversationIdRef.current = currentConversationId; }, [currentConversationId]);
  useEffect(() => { currentOrgIdRef.current = currentOrgId; }, [currentOrgId]);
  useEffect(() => { setCurrentConversationIdRef.current = setCurrentConversationId; }, [setCurrentConversationId]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { userAISettingsRef.current = userAISettings; }, [userAISettings]);
  useEffect(() => { geminiOAuthTokenRef.current = geminiOAuthToken; }, [geminiOAuthToken]);
  useEffect(() => { isGeminiOAuthConnectedRef.current = isGeminiOAuthConnected; }, [isGeminiOAuthConnected]);
  useEffect(() => { selectedModelRef.current = selectedModel; }, [selectedModel]);
  useEffect(() => { selectedProviderRef.current = selectedProvider; }, [selectedProvider]);
  useEffect(() => { reasoningEffortRef.current = reasoningEffort; }, [reasoningEffort]);
  useEffect(() => { autoModeRef.current = autoMode; }, [autoMode]);
  useEffect(() => { agentContextRef.current = agentContext; }, [agentContext]);
  useEffect(() => { summarizedContextRef.current = summarizedContext; }, [summarizedContext]);
  useEffect(() => { orchestratorDataRef.current = orchestratorData; }, [orchestratorData]);
  useEffect(() => { selectedNotebookIdRef.current = selectedNotebookId; }, [selectedNotebookId]);

  // Knowledge Workbench → Cortex handoff
  useEffect(() => {
    if (!pendingNotebookHandoff?.notebookId) return;
    setSelectedNotebookId(pendingNotebookHandoff.notebookId);
    if (pendingNotebookHandoff.summary) {
      setSummarizedContext(pendingNotebookHandoff.summary);
    }
    clearNotebookHandoff();
  }, [pendingNotebookHandoff, clearNotebookHandoff]);

  // Persist notebook grounding on the active chat session
  useEffect(() => {
    if (!currentConversationId) return;
    void supabase
      .from('chat_sessions')
      .update({ notebook_id: selectedNotebookId ?? null } as { notebook_id: string | null })
      .eq('id', currentConversationId);
  }, [selectedNotebookId, currentConversationId]);

  // Ref for current messages — used by callbacks that need the latest messages
  // without depending on tanstackChat (which would cause re-renders)
  // (declared above with conversation state for stream-first token handler)

  const buildBrainRequestBody = useCallback(() => {
    const currentOrchestratorData = orchestratorDataRef.current;
    const actionItems: { id: string; text: string; completed: boolean; priority: string; subtaskCount: number; subtasksCompleted: number }[] = [];
    for (const [, data] of Object.entries(currentOrchestratorData)) {
      if (data.todoList?.items) {
        for (const item of data.todoList.items) {
          actionItems.push({
            id: item.id,
            text: item.text,
            completed: item.completed,
            priority: item.priority ?? 'medium',
            subtaskCount: item.subtasks?.length ?? 0,
            subtasksCompleted: item.subtasks?.filter(s => s.completed).length ?? 0,
          });
        }
      }
    }

    return {
      userId: userRef.current?.id,
      sessionId: currentConversationIdRef.current,
      ...(durableStreamTransport ? { transport: "durable_stream" as const } : {}),
      settings: {
        ...userAISettingsRef.current,
        model: selectedModelRef.current,
        primaryProvider: selectedProviderRef.current,
        primaryModel: selectedProviderRef.current === 'openrouter'
          ? selectedModelRef.current
          : resolveGeminiModelId(selectedModelRef.current),
        autoMode: autoModeRef.current,
        reasoningEffort: reasoningEffortRef.current,
        // BYOK resolved server-side from profiles; do not send API key material in body.
        geminiApiKey: undefined,
        openrouterApiKey: undefined,
        ...(isGeminiOAuthConnectedRef.current && userAISettingsRef.current.useGoogleGemini !== false && geminiOAuthTokenRef.current
          ? { geminiAccessToken: geminiOAuthTokenRef.current }
          : {}),
      },
      agentContext: {
        ...agentContextRef.current,
        // Ensure the request is scoped to the currently selected organization.
        // useAgentCapabilities can lag behind the org switcher on first load.
        organizationId: currentOrgId ?? agentContextRef.current?.organizationId,
        // Project context enables project-scoped memory retrieval.
        selectedProjectId: currentProjectRef.current?.id ?? agentContextRef.current?.selectedProjectId,
      },
      summarizedContext: summarizedContextRef.current,
      actionItemsContext: actionItems.length > 0 ? {
        items: actionItems,
        totalCount: actionItems.length,
        completedCount: actionItems.filter(i => i.completed).length,
      } : undefined,
      ...perMessageDataRef.current,
      ...(selectedNotebookIdRef.current ? { notebookId: selectedNotebookIdRef.current } : {}),
    };
  }, [durableStreamTransport]);

  const handleBrainAwaiting = useCallback((response: BrainAwaitingResponse) => {
    // ask_user pause — render a question card, resume via /brain/resume.
    if (response.status === 'awaiting_input' && response.question) {
      setPendingQuestion({
        runId: response.runId,
        question: response.question,
        options: response.options,
        allowFreeText: response.allowFreeText,
      });
      synapticAwaitingRef.current = response;
      return;
    }

    synapticAwaitingRef.current = response;
    setSynapticRunId(response.runId);
    setSynapticSessionId(response.sessionId ?? currentConversationIdRef.current);

    const lastAssistant = [...messagesRef.current].reverse().find((m) => m.role === 'assistant');
    if (lastAssistant) {
      setOrchestratorData((prev) => ({
        ...prev,
        [lastAssistant.id]: {
          ...prev[lastAssistant.id],
          isThinking: true,
          statusMessage: response.message ?? `Running ${response.specialistType ?? 'specialist'}…`,
        },
      }));
    }
    toast.message('Working in the background', {
      description: response.message ?? 'You will be notified when the response is ready.',
    });
  }, []);

  /**
   * Answer an awaiting_input run — POST /resume flips it to
   * specialist_complete; the pg_net wake then resumes respond and the
   * completed response_output arrives via the run watcher.
   */
  const answerPendingQuestion = useCallback(
    async (opts: { approved?: boolean; value?: string }) => {
      const q = pendingQuestion;
      if (!q) return;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error('Sign in required');
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/brain/resume`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ runId: q.runId, approved: opts.approved !== false, value: opts.value }),
          },
        );
        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(errBody.error ?? `Resume failed (${res.status})`);
        }
        // Hand off to the run watcher for completion.
        setPendingQuestion(null);
        setSynapticRunId(q.runId);
        setSynapticSessionId(synapticAwaitingRef.current?.sessionId ?? currentConversationIdRef.current);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Resume failed');
      }
    },
    [pendingQuestion],
  );

  const connection = useMemo(
    () =>
      createBrainSynapticConnection(BRAIN_URL, async () => {
        const token = await getAuthToken();
        const userId = userRef.current?.id;
        if (
          userId &&
          isGeminiOAuthConnectedRef.current &&
          userAISettingsRef.current.useGoogleGemini !== false
        ) {
          const tokenResult = await geminiAuthService.getValidToken(userId);
          if (tokenResult.success && tokenResult.data?.access_token) {
            geminiOAuthTokenRef.current = tokenResult.data.access_token;
          }
        }
        // Last line of defense: create session in the request factory so every
        // send path (handleSendMessage or raw sendMessage) gets a real UUID.
        if (userId && !currentConversationIdRef.current) {
          const sessionId = await ensureChatSession(
            userId,
            undefined,
            currentOrgIdRef.current,
          );
          if (sessionId) {
            currentConversationIdRef.current = sessionId;
            setCurrentConversationIdRef.current(sessionId);
          }
        }
        const body = buildBrainRequestBody();
        perMessageDataRef.current = {};
        if (!body.sessionId) {
          logger.warn('Brain request missing sessionId — lease will use anon-* fallback');
        }
        return {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body,
        };
      }, handleBrainAwaiting),
    [BRAIN_URL, buildBrainRequestBody, handleBrainAwaiting],
  );

  const getAssistantText = (msg: UIMessage): string =>
    msg.parts
      .filter((p) => p.type === 'text')
      .map((p) => ('content' in p ? (p as { type: 'text'; content: string }).content : ''))
      .join('');

  const setTanstackMessagesRef = useRef<(messages: UIMessage[]) => void>(() => {});

  const tanstackChat = useTanstackChat({
    connection,
    // Process AG-UI stream chunks for orchestrator status updates
    onChunk: (chunk: BrainChunk) => {
      // Helper: find the current assistant message from the latest React state.
      // Due to TanStack AI v0.5+ lazy message creation and React batched state
      // updates, this may return undefined for chunks that arrive before the
      // assistant UIMessage is committed to messagesRef.
      const findLastAssistant = (): UIMessage | undefined => {
        const arr = messagesRef.current;
        for (let i = arr.length - 1; i >= 0; i--) {
          if (arr[i].role === 'assistant') {
            return arr[i];
          }
        }
        return undefined;
      };

      // Track the in-flight cortex run so a dropped transport can recover it.
      if (chunk.type === 'RUN_STARTED') {
        activeCortexRunRef.current = chunk.cortexRunId ?? null;
        intentionalStopRef.current = false;
      }
      if (chunk.type === 'RUN_FINISHED') {
        activeCortexRunRef.current = null;
      }

      // Handle AG-UI stream errors (brain RUN_ERROR events)
      if (chunk.type === 'RUN_ERROR') {
        const msg = chunk.error?.message ?? 'Chat request failed';
        lastBrainErrorRef.current = msg;
        const summary = formatBrainUserErrorSummary(new Error(msg));
        logger.error('Brain RUN_ERROR', { message: msg });
        toast.error(summary);
        const lastAssistant = findLastAssistant();
        if (lastAssistant) {
          const replacement = resolveAssistantErrorText(
            getAssistantText(lastAssistant),
            msg,
            byokNoticeShownRef.current,
          );
          if (replacement) {
            patchAssistantMessageRef.current(lastAssistant.id, replacement);
          }
          setOrchestratorData((prev) => ({
            ...prev,
            [lastAssistant.id]: {
              ...prev[lastAssistant.id],
              isThinking: false,
              statusMessage: undefined,
            },
          }));
        }
        return;
      }

      // ── Shared reducer fold ────────────────────────────────────────────
      // All timeline-event semantics (status, tool, sub-agent, plan, question)
      // live in cortexStreamReducer — the same fold used for cortex_run_events
      // rehydration. Here we project the run's accumulated meta onto the last
      // assistant message so the drawer's per-message renderers keep working.
      const isOrchestrationChunk =
        (chunk.type === 'CUSTOM' &&
          (chunk.name === 'orchestrator-status' ||
            chunk.name === 'tool-activity' ||
            chunk.name === 'sub-agent-activity' ||
            chunk.name === 'plan' ||
            chunk.name === 'user-question')) ||
        chunk.type === 'RUN_STARTED' ||
        chunk.type === 'RUN_FINISHED';

      if (isOrchestrationChunk) {
        // Fold mutates in place — keep the canonical timeline on a ref so
        // resolvedRun is available synchronously, then copy to trigger
        // subscribers (workbench consumers read `liveTimeline`).
        const tl = reduceBrainChunk(liveTimelineRef.current, chunk);
        setLiveTimeline({ ...tl });
        const resolvedRun = latestRun(tl);

        // Project run meta onto the assistant message render path.
        if (resolvedRun && chunk.type === 'CUSTOM') {
          const runMeta = runToOrchestratorMeta(resolvedRun);
          const lastAssistant = findLastAssistant();
          if (lastAssistant) {
            setOrchestratorData((prev) => ({
              ...prev,
              [lastAssistant.id]: {
                ...prev[lastAssistant.id],
                isThinking: resolvedRun!.lifecycle === 'running',
                statusMessage: runMeta.statusMessage,
                ...(runMeta.modelName ? { modelName: runMeta.modelName } : {}),
                ...(runMeta.modelTier ? { modelTier: runMeta.modelTier } : {}),
                ...(runMeta.thinkingSteps ? { thinkingSteps: runMeta.thinkingSteps } : {}),
                ...(typeof runMeta.currentStep === 'number' ? { currentStep: runMeta.currentStep } : {}),
                ...(resolvedRun!.toolActivity.entries.length ? { toolActivity: runMeta.toolActivity } : {}),
                ...(runMeta.subAgentActivity ? { subAgentActivity: runMeta.subAgentActivity } : {}),
              },
            }));
            pendingOrchestratorRef.current = null;
          } else {
            pendingOrchestratorRef.current = {
              ...pendingOrchestratorRef.current,
              isThinking: true,
              statusMessage: runMeta.statusMessage ?? 'Thinking...',
              ...(runMeta.modelName ? { modelName: runMeta.modelName } : {}),
              ...(runMeta.modelTier ? { modelTier: runMeta.modelTier } : {}),
              ...(runMeta.thinkingSteps ? { thinkingSteps: runMeta.thinkingSteps } : {}),
              ...(typeof runMeta.currentStep === 'number' ? { currentStep: runMeta.currentStep } : {}),
              ...(resolvedRun!.toolActivity.entries.length ? { toolActivity: runMeta.toolActivity } : {}),
              ...(runMeta.subAgentActivity ? { subAgentActivity: runMeta.subAgentActivity } : {}),
            };
          }
        }
      }

      // OAuth quota → BYOK fallback (once per assistant turn)
      if (isGeminiByokFallbackChunk(chunk) && !byokNoticeShownRef.current) {
        byokNoticeShownRef.current = true;
        toast.info(GEMINI_BYOK_FALLBACK_COPY.title, {
          description: GEMINI_BYOK_FALLBACK_COPY.description,
        });
      }

      // Handle structured specialist data for rich client-side rendering (health plans, etc.)
      if (chunk.type === 'CUSTOM' && chunk.name === 'specialist-data' && chunk.data) {
        const lastAssistant = findLastAssistant();
        if (lastAssistant) {
          setOrchestratorData(prev => ({
            ...prev,
            [lastAssistant.id]: {
              ...prev[lastAssistant.id],
              specialistData: chunk.data as unknown as SpecialistData,
            },
          }));
        }
      }
    },
    onFinish: (message: UIMessage) => {
      // Extract text content from the completed message
      let messageText = message.parts
        .filter((p) => p.type === 'text')
        .map((p) => 'content' in p ? (p as { type: 'text'; content: string }).content : '')
        .join('');

      const replacement = resolveAssistantErrorText(
        messageText,
        lastBrainErrorRef.current,
        byokNoticeShownRef.current,
      );
      if (replacement && replacement !== messageText) {
        messageText = replacement;
        patchAssistantMessageRef.current(message.id, replacement);
      }
      lastBrainErrorRef.current = null;

      logger.info('✅ Message completed:', { messageId: message.id });

      // Clear thinking state
      setOrchestratorData(prev => ({
        ...prev,
        [message.id]: {
          ...prev[message.id],
          isThinking: false,
        },
      }));

      // Save message to database
      const currentUser = userRef.current;
      if (isAuthenticated && currentUser?.id) {
        const extendedMessage = convertUIMessageToExtended(message, orchestratorDataRef.current[message.id]);
        saveMessageToDb(
          extendedMessage,
          isAuthenticated,
          currentUser.id,
          currentConversationId,
          setCurrentConversationId,
          currentOrgId
        ).then((dbMessageId) => {
          // Call task-extractor edge function only when autoExtractTasks is enabled
          if (autoExtractTasks && message.role === 'assistant' && messageText.length > 50) {
            extractTasksFromMessage(messageText, message.id, dbMessageId).catch((err: unknown) => {
              logger.warn('Task extraction failed (non-critical):', { error: err });
            });
          }
        }).catch((error: unknown) => {
          logger.error('Failed to save message:', { error });
        });
      }

      // Set suggested follow-up questions for assistant messages
      if (message.role === 'assistant' && messageText.trim().length > 20) {
        const suggestions = getFollowUpSuggestions(messageText);
        setOrchestratorData(prev => ({
          ...prev,
          [message.id]: {
            ...prev[message.id],
            followUpQuestions: suggestions,
          },
        }));
      }
    },
    onError: (error: Error) => {
      pendingOrchestratorRef.current = null;
      lastBrainErrorRef.current = error.message;
      logger.error('❌ Chat error:', { error });

      // SSE transport dropped mid-run — the brain keeps checkpointing
      // cortex_runs server-side, so attach the run watcher and recover
      // response_output when it completes instead of losing the response.
      const droppedRunId = activeCortexRunRef.current;
      activeCortexRunRef.current = null;
      if (droppedRunId && !intentionalStopRef.current) {
        const sessionId = currentConversationIdRef.current;
        synapticAwaitingRef.current = {
          runId: droppedRunId,
          status: 'awaiting',
          sessionId: sessionId ?? undefined,
        };
        setSynapticRunId(droppedRunId);
        setSynapticSessionId(sessionId ?? undefined);
        toast.message('Connection lost — recovering run', {
          description: 'The task is still running; the result will appear when it finishes.',
        });
        return;
      }
      toast.error(formatBrainUserErrorSummary(error));
    },
  });

  patchAssistantMessageRef.current = (messageId: string, newContent: string) => {
    tanstackChat.setMessages(
      tanstackChat.messages.map((m) => {
        if (m.id !== messageId) return m;
        const textIdx = m.parts.findIndex((p) => p.type === 'text');
        if (textIdx === -1) {
          return {
            ...m,
            parts: [{ type: 'text' as const, content: newContent }, ...m.parts],
          };
        }
        return {
          ...m,
          parts: m.parts.map((p, i) =>
            i === textIdx && p.type === 'text' ? { ...p, content: newContent } : p,
          ),
        };
      }),
    );
  };

  // Keep messagesRef in sync with TanStack chat messages
  useEffect(() => { messagesRef.current = tanstackChat.messages; }, [tanstackChat.messages]);

  // Flush buffered orchestrator status to the real assistant message once it appears.
  // This bridges the gap between CUSTOM events arriving (via onChunk) and React
  // committing the new assistant message from TanStack's lazy StreamProcessor.
  useEffect(() => {
    if (!pendingOrchestratorRef.current) {
      return;
    }
    const msgs = tanstackChat.messages;
    let lastAssistant: UIMessage | undefined;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') {
        lastAssistant = msgs[i];
        break;
      }
    }
    if (lastAssistant) {
      const pending = pendingOrchestratorRef.current;
      pendingOrchestratorRef.current = null;
      const assistantId = lastAssistant.id;
      setOrchestratorData(prev => ({
        ...prev,
        [assistantId]: {
          ...prev[assistantId],
          ...pending,
        },
      }));
    }
  }, [tanstackChat.messages]);

  setTanstackMessagesRef.current = (messages) => {
    tanstackChat.setMessages(messages);
  };

  const appendAssistantFromSynapticRun = useCallback((content: string) => {
    const msgId = generateMessageId();
    const uiMsg: UIMessage = {
      id: msgId,
      role: 'assistant',
      parts: [{ type: 'text', content }],
    };
    setTanstackMessagesRef.current([...messagesRef.current, uiMsg]);
    setOrchestratorData((prev) => ({
      ...prev,
      [msgId]: {
        isThinking: false,
        statusMessage: 'Complete',
      },
    }));
    if (isAuthenticated && userRef.current?.id) {
      const extended = convertUIMessageToExtended(uiMsg);
      saveMessageToDb(
        extended,
        isAuthenticated,
        userRef.current.id,
        currentConversationIdRef.current,
        setCurrentConversationId,
        currentOrgId,
      ).catch((err: unknown) => logger.error('Failed to save synaptic assistant message:', { error: err }));
    }
    setSynapticRunId(undefined);
    setSynapticSessionId(undefined);
    synapticAwaitingRef.current = null;
  }, [isAuthenticated, currentOrgId]);

  useCortexRunAwait(supabase, synapticSessionId, synapticRunId, {
    enabled: Boolean(synapticRunId && synapticSessionId),
    onComplete: (run: CortexRunRow) => {
      const text = run.response_output ?? synapticAwaitingRef.current?.message ?? 'Task completed.';
      const last = messagesRef.current.at(-1);
      if (last?.role === 'assistant') {
        // Recovered foreground run — the partial streamed assistant is the
        // last message; patch it rather than appending a duplicate.
        patchAssistantMessageRef.current(last.id, text);
        setOrchestratorData((prev) => ({
          ...prev,
          [last.id]: { ...prev[last.id], isThinking: false, statusMessage: 'Complete' },
        }));
        if (isAuthenticated && userRef.current?.id) {
          const patched: UIMessage = { ...last, parts: [{ type: 'text', content: text }] };
          saveMessageToDb(
            convertUIMessageToExtended(patched, orchestratorDataRef.current[last.id]),
            isAuthenticated,
            userRef.current.id,
            currentConversationIdRef.current,
            setCurrentConversationId,
            currentOrgId,
          ).catch((err: unknown) => logger.error('Failed to save recovered assistant message:', { error: err }));
        }
        setSynapticRunId(undefined);
        setSynapticSessionId(undefined);
        synapticAwaitingRef.current = null;
      } else {
        appendAssistantFromSynapticRun(text);
      }
      toast.success('Background task complete');
    },
    onFailed: (run: CortexRunRow) => {
      setSynapticRunId(undefined);
      setSynapticSessionId(undefined);
      toast.error(run.error_message ?? 'Background task failed');
    },
  });

  // Scan for any pending/running background jobs when active session changes
  useEffect(() => {
    if (!currentConversationId || !supabase) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("cortex_runs")
          .select("id, status, specialist_result")
          .eq("session_id", currentConversationId)
          .in("status", ["awaiting", "in_progress", "awaiting_input"])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!cancelled && data) {
          if (data.status === "awaiting_input") {
            // Recover a pending ask_user question across reloads.
            const sr = data.specialist_result as Record<string, unknown> | null;
            if (sr?._awaitingInput === true && typeof sr.question === "string") {
              setPendingQuestion({
                runId: data.id,
                question: sr.question,
                options: sr.options as string[] | undefined,
                allowFreeText: sr.allowFreeText !== false,
              });
            }
          } else {
            setSynapticRunId(data.id);
            setSynapticSessionId(currentConversationId);
          }
        }
      } catch (_err) {
        // Non-critical background scan failure
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentConversationId]);

  // Convert TanStack messages to Extended format with orchestrator data
  const isStreaming = tanstackChat.isLoading;

  // Determine if TanStack has produced an assistant message for the current stream.
  // Due to lazy creation, the message may not exist yet during the thinking phase.
  const lastTanstackMsg = tanstackChat.messages.at(-1);
  const hasStreamingAssistant = isStreaming && lastTanstackMsg?.role === 'assistant';
  const needsSyntheticThinking = isStreaming && !hasStreamingAssistant;

  const messages: ExtendedMessage[] = useMemo(() => [
    // Welcome message shown only when starting a new/empty conversation
    ...(tanstackChat.messages.length === 0 ? [{
      role: "assistant" as const,
      content: agentBranding.welcomeMessage,
      id: "initial-message"
    }] : []),
    // Convert all messages, marking the last assistant message as typing during streaming
    ...tanstackChat.messages.map((msg, index) => {
      const msgData = orchestratorData[msg.id];
      const extended = convertUIMessageToExtended(msg, msgData, msgData?.todoList);
      // Pass through task extraction loading state
      if (msgData?.isExtractingTasks) {
        extended.isExtractingTasks = true;
      }
      if (msgData?.followUpQuestions?.length) {
        extended.followUpQuestions = msgData.followUpQuestions;
      }
      // Pass through structured specialist data for rich rendering
      if (msgData?.specialistData) {
        extended.specialistData = msgData.specialistData;
      }
      if (msgData?.toolActivity) {
        extended.toolActivity = msgData.toolActivity;
        extended.orchestratorMeta = {
          ...extended.orchestratorMeta,
          toolActivity: msgData.toolActivity,
        };
      }
      // Mark the last assistant message as typing while streaming
      const isLastMessage = index === tanstackChat.messages.length - 1;
      if (isLastMessage && msg.role === 'assistant' && isStreaming) {
        extended.isTyping = true;
        // If no content yet, set orchestrator thinking state
        if (!extended.content) {
          extended.orchestratorMeta = {
            ...extended.orchestratorMeta,
            isThinking: true,
            statusMessage: orchestratorData[msg.id]?.statusMessage
              ?? pendingOrchestratorRef.current?.statusMessage
              ?? 'Thinking...',
            ...(orchestratorData[msg.id]?.modelName ? { modelName: orchestratorData[msg.id].modelName } : {}),
            ...(orchestratorData[msg.id]?.modelTier ? { modelTier: orchestratorData[msg.id].modelTier } : {}),
            ...(orchestratorData[msg.id]?.thinkingSteps
              ? { thinkingSteps: orchestratorData[msg.id].thinkingSteps }
              : pendingOrchestratorRef.current?.thinkingSteps
                ? { thinkingSteps: pendingOrchestratorRef.current.thinkingSteps }
                : {}),
            ...(typeof orchestratorData[msg.id]?.currentStep === 'number'
              ? { currentStep: orchestratorData[msg.id].currentStep }
              : typeof pendingOrchestratorRef.current?.currentStep === 'number'
                ? { currentStep: pendingOrchestratorRef.current.currentStep }
                : {}),
            toolActivity:
              orchestratorData[msg.id]?.toolActivity
              ?? pendingOrchestratorRef.current?.toolActivity
              ?? extended.orchestratorMeta?.toolActivity,
            subAgentActivity:
              orchestratorData[msg.id]?.subAgentActivity
              ?? pendingOrchestratorRef.current?.subAgentActivity
              ?? extended.orchestratorMeta?.subAgentActivity,
          };
        }
      }
      return extended;
    }),
    // Synthetic thinking message: shown while streaming is active but the assistant
    // UIMessage hasn't been created yet (TanStack AI v0.5+ lazy creation gap).
    // This ensures CortexThinkingAnimation always renders during the planning phase.
    ...(needsSyntheticThinking ? [{
      id: 'thinking-placeholder',
      role: 'assistant' as const,
      content: '',
      isTyping: true,
      toolActivity: pendingOrchestratorRef.current?.toolActivity,
      orchestratorMeta: {
        isThinking: true,
        statusMessage: pendingOrchestratorRef.current?.statusMessage ?? 'Thinking...',
        modelName: pendingOrchestratorRef.current?.modelName,
        modelTier: pendingOrchestratorRef.current?.modelTier,
        thinkingSteps: pendingOrchestratorRef.current?.thinkingSteps,
        currentStep: pendingOrchestratorRef.current?.currentStep,
        subAgentActivity: pendingOrchestratorRef.current?.subAgentActivity,
        toolActivity: pendingOrchestratorRef.current?.toolActivity,
      },
    }] : []),
  ], [tanstackChat.messages, orchestratorData, isStreaming, needsSyntheticThinking]);

  // Load session list
  useEffect(() => {
    const loadSessions = async () => {
      if (!isAuthenticated || !user?.id) {return;}
      
      try {
        let query = supabase
          .from('chat_sessions')
          .select('id, title, created_at, organization_id')
          .eq('user_id', user.id);
        
        // Scope to current organization if available
        if (currentOrgId) {
          query = query.eq('organization_id', currentOrgId);
        }
        
        const { data, error } = await query
          .order('created_at', { ascending: false });
          
        if (error) {
          logger.error("Error loading sessions:", { error: error });
          toast.error('Failed to load sessions');
          return;
        }
        
        if (data) {
          setConversationList(data.map((conversation) => ({
            ...conversation,
            organization_id: conversation.organization_id ?? undefined,
          })) as Conversation[]);
        }
      } catch (error) {
        logger.error("Failed to load session list:", { error: error });
        toast.error('Failed to load sessions');
      }
    };
    
    loadSessions();
  }, [isAuthenticated, user, currentOrgId]);

  // Scroll to bottom within the chat message list only — never scrollIntoView (scrolls the page).
  const scrollToBottom = useCallback(() => {
    const container = document.querySelector<HTMLElement>(
      "[data-testid='cortex-message-scroll']",
    );
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  // Apply pending messages after conversation switch
  useEffect(() => {
    if (pendingMessagesRef.current) {
      const msgs = pendingMessagesRef.current;
      pendingMessagesRef.current = null;
      tanstackChat.setMessages(msgs);
      setTimeout(scrollToBottom, 100);
    }
  }, [currentConversationId, tanstackChat, scrollToBottom]);

  // Load existing session
  const loadSession = useCallback(async (conversationId: string) => {
    if (!isAuthenticated || !user?.id) {return;}

    setIsLoadingConversation(true);
    try {
      const { data: _session, error: convError } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('id', conversationId)
        .eq('user_id', user.id)
        .single();

      if (convError || (_session && currentOrgId && _session.organization_id !== currentOrgId)) {
        logger.warn("Session not found or belongs to another organization:", { conversationId, sessionOrg: _session?.organization_id, currentOrgId });
        startNewConversation();
        setIsLoadingConversation(false);
        return;
      }

      const sessionRow = _session as { notebook_id?: string | null };
      if (sessionRow.notebook_id) {
        setSelectedNotebookId(sessionRow.notebook_id);
      }

      const { data: messagesData, error: msgError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (msgError) {
        logger.error("Error loading messages:", { error: msgError });
        toast.error('Failed to load session messages');
        setIsLoadingConversation(false);
        return;
      }

      // Hide welcome animation
      setShowWelcomeAnimation(false);
      
      // Build UI messages from DB data AND restore todoList from metadata
      const restoredOrchestratorData: Record<string, OrchestratorMeta & { todoList?: ConversationTodoList }> = {};
      
      const uiMessages: UIMessage[] = ((messagesData || []) as Array<{ id: string; role: string; content: string; metadata?: Record<string, unknown> | null }>).map(msg => {
        // Restore todoList from metadata JSONB if present
        const metadata = msg.metadata ?? null;
        if (metadata?.todoList && msg.role === 'assistant') {
          restoredOrchestratorData[msg.id] = {
            todoList: metadata.todoList as ConversationTodoList,
          };
        }
        
        return {
          id: msg.id,
          role: msg.role as 'user' | 'assistant',
          parts: [{ type: 'text' as const, content: msg.content }],
        };
      });

      // Set restored orchestrator data (replaces empty state)
      setOrchestratorData(restoredOrchestratorData);

      // Store pending messages in ref, then trigger state update
      // The useEffect above will apply them after the re-render
      pendingMessagesRef.current = uiMessages;
      setCurrentConversationId(conversationId);
      
      // Also set messages directly in case the effect doesn't fire (same conversation ID)
      tanstackChat.setMessages(uiMessages);

      // Scroll to bottom after loading
      setTimeout(scrollToBottom, 150);

      toast.success('Session loaded');
    } catch (error) {
      logger.error("Failed to load session:", { error: error });
      toast.error('Failed to load session');
    } finally {
      setIsLoadingConversation(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- startNewConversation is defined after loadSession and captured by closure
  }, [isAuthenticated, user, tanstackChat, setShowWelcomeAnimation, scrollToBottom, currentOrgId]);

  // Start new conversation
  const startNewConversation = useCallback(() => {
    setCurrentConversationId(undefined);
    tanstackChat.setMessages([]);
    setOrchestratorData({});
    setPendingAttachments([]);
    setSelectedContextIds([]);
    setSummarizedContext(undefined);
    setShowWelcomeAnimation(true);
  }, [tanstackChat]);

  const prevOrgIdRef = useRef<string | undefined>(currentOrgId);
  const hasRestoredOrgRef = useRef<string | null>(null);
  const loadSessionRef = useRef(loadSession);
  loadSessionRef.current = loadSession;
  const startNewConversationRef = useRef(startNewConversation);
  startNewConversationRef.current = startNewConversation;

  // Switch conversation context automatically when user changes organization
  useEffect(() => {
    if (!currentOrgId) return;

    // Detect organization change
    if (prevOrgIdRef.current && prevOrgIdRef.current !== currentOrgId) {
      const oldOrgId = prevOrgIdRef.current;
      prevOrgIdRef.current = currentOrgId;
      hasRestoredOrgRef.current = currentOrgId;
      logger.info('🔄 Organization switched in Cortex chat:', { from: oldOrgId, to: currentOrgId });

      try {
        const savedId = localStorage.getItem(`last-conversation-${currentOrgId}`);
        if (savedId) {
          loadSessionRef.current(savedId);
        } else {
          startNewConversationRef.current();
        }
      } catch (e) {
        logger.warn('Failed to load session for new org', { error: e });
        startNewConversationRef.current();
      }

      if (currentOrganization?.name) {
        toast.info(`Switched to ${currentOrganization.name}`, {
          description: 'Cortex chat updated for active organization',
          duration: 3000,
        });
      }
    } else {
      prevOrgIdRef.current = currentOrgId;
      // Initial mount restore (guarded per org to prevent infinite re-render loops)
      if (!currentConversationId && hasRestoredOrgRef.current !== currentOrgId) {
        hasRestoredOrgRef.current = currentOrgId;
        try {
          const savedId = localStorage.getItem(`last-conversation-${currentOrgId}`);
          if (savedId) {
            logger.info('🔄 Restoring last conversation for org:', { currentOrgId, savedId });
            loadSessionRef.current(savedId);
          }
        } catch (e) {
          logger.warn('Failed to restore last conversation', { error: e });
        }
      }
    }
  }, [currentOrgId, currentOrganization?.name, currentConversationId]);

  // Enhanced send message wrapper
  const handleSendMessage = useCallback(async (text?: string) => {
    const messageText = text ?? input;
    if (!messageText.trim()) {return;}

    logger.info('🚀 Sending message:', { messageText });
    logger.info('📎 Attachments:', { attachments: pendingAttachments });
    logger.info('🔍 Context IDs:', { contextIds: selectedContextIds });

    // Hide welcome animation after first message
    if (showWelcomeAnimation) {
      setShowWelcomeAnimation(false);
    }

    try {
      pendingOrchestratorRef.current = null;
      byokNoticeShownRef.current = false;
      lastBrainErrorRef.current = null;

      // Create chat_sessions BEFORE the brain call so sessionId is a real UUID
      // (not anon-*). Sync the ref immediately — setState alone is too late for
      // buildBrainRequestBody in the same tick.
      let sessionIdForSend = currentConversationIdRef.current ?? currentConversationId;
      if (isAuthenticated && user?.id && !sessionIdForSend) {
        sessionIdForSend = await ensureChatSession(
          user.id,
          undefined,
          currentOrgId,
          messageText,
        );
        if (sessionIdForSend) {
          currentConversationIdRef.current = sessionIdForSend;
          setCurrentConversationId(sessionIdForSend);
        }
      }

      // Save user message to database before sending
      if (isAuthenticated && user?.id) {
        const userMsg: ExtendedMessage = {
          id: generateMessageId(),
          role: 'user',
          content: messageText,
        };
        saveMessageToDb(
          userMsg,
          isAuthenticated,
          user.id,
          sessionIdForSend,
          setCurrentConversationId,
          currentOrgId
        ).catch((err: unknown) => logger.error('Failed to save user message:', { error: err }));
      }

      // Set per-message extras in the ref before sending (read by the connection callback)
      perMessageDataRef.current = {
        attachments: pendingAttachments,
        contextIds: selectedContextIds,
        specialist: selectedSpecialist,
      };

      // TanStack AI sendMessage takes a plain string
      await tanstackChat.sendMessage(messageText);

      pendingOrchestratorRef.current = null;
      
      logger.info('✅ Message sent successfully', { specialist: selectedSpecialist });
      
      // Clear input after successful send
      setInput('');
      
      // Clear pending attachments and specialist selection
      setPendingAttachments([]);
      setSelectedSpecialist(undefined);
    } catch (error) {
      pendingOrchestratorRef.current = null;
      perMessageDataRef.current = {};
      logger.error('❌ Failed to send message:', { error: error });
      toast.error('Failed to send message');
    }
  }, [input, tanstackChat, pendingAttachments, selectedContextIds, selectedSpecialist, showWelcomeAnimation, setInput, isAuthenticated, user, currentConversationId, currentOrgId, setCurrentConversationId]);

  // File upload handler
  const handleFileUploaded = useCallback((attachment: MessageAttachment) => {
    setPendingAttachments(prev => [...prev, attachment]);
  }, []);

  // Update message todo list — persists to orchestratorData AND chat_messages.metadata
  const updateMessageTodoList = useCallback((messageId: string, items: ConversationTodoItem[]) => {
    // Store todo list in orchestrator metadata for the message
    setOrchestratorData(prev => {
      const existing = prev[messageId]?.todoList;
      const updatedTodoList: ConversationTodoList = {
        id: existing?.id ?? `todo_${Date.now()}`,
        title: existing?.title ?? agentBranding.actionItemsTitle,
        items,
        context: existing?.context ?? 'Updated from conversation',
        createdAt: existing?.createdAt ?? new Date().toISOString(),
      };

      // Cast the table builder to bypass database constraints for the un-synchronized metadata column
      const chatMessagesTable = supabase.from('chat_messages') as unknown as {
        update: (values: { metadata: { todoList: ConversationTodoList } }) => {
          eq: (column: string, value: string) => Promise<{ error: { message: string } | null }>;
        };
      };

      // Persist to chat_messages.metadata in the background
      chatMessagesTable
        .update({ metadata: { todoList: updatedTodoList } })
        .eq('id', messageId)
        .then(({ error }) => {
          if (error) {
            logger.warn('Failed to persist todoList update:', { error });
          }
        });

      return {
        ...prev,
        [messageId]: {
          ...prev[messageId],
          todoList: updatedTodoList,
        }
      };
    });
  }, []);

  // Add assistant message directly
  const addAssistantMessage = useCallback(async (content: string) => {
    const msg: UIMessage = {
      role: "assistant",
      id: generateMessageId(),
      parts: [{ type: 'text', content: content }],
    };
    
    tanstackChat.setMessages([...messagesRef.current, msg]);
    
    // Save to database
    if (isAuthenticated && user?.id) {
      const extendedMsg = convertUIMessageToExtended(msg);
      await saveMessageToDb(
        extendedMsg,
        isAuthenticated,
        user.id,
        currentConversationId,
        setCurrentConversationId,
        currentOrgId
      );
    }
  }, [tanstackChat, isAuthenticated, user, currentConversationId, currentOrgId]);

  // Clear input when a message is being sent
  useEffect(() => {
    if (tanstackChat.isLoading) {
      setInput('');
    }
  }, [tanstackChat.isLoading]);

  // Conversation management helpers
  const deleteConversation = useCallback(async (conversationId: string) => {
    if (!isAuthenticated || !user?.id) {return;}
    
    try {
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', conversationId)
        .eq('user_id', user.id);
      
      if (error) {throw error;}
      
      // Refresh conversation list
      let refreshQuery = supabase
        .from('chat_sessions')
        .select('id, title, created_at, organization_id')
        .eq('user_id', user.id);
      
      if (currentOrgId) {
        refreshQuery = refreshQuery.eq('organization_id', currentOrgId);
      }
      
      const { data } = await refreshQuery
        .order('created_at', { ascending: false });
      
      if (data) {
        setConversationList(data.map((conversation) => ({
          ...conversation,
          organization_id: conversation.organization_id ?? undefined,
        })));
      }
      
      // If deleted current conversation, start new one
      if (conversationId === currentConversationId) {
        startNewConversation();
      }
      
      toast.success('Conversation deleted');
    } catch (error) {
      logger.error('Failed to delete conversation:', { error: error });
      toast.error('Failed to delete conversation');
    }
  }, [isAuthenticated, user, currentConversationId, startNewConversation, currentOrgId]);

  const toggleContextSelection = useCallback((conversationId: string) => {
    setSelectedContextIds(prev => 
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  }, []);

  // Summarize selected conversations
  const summarizeSelectedConversations = useCallback(async () => {
    if (selectedContextIds.length === 0) {
      toast.error('Please select conversations to summarize');
      return;
    }
    
    setIsSummarizing(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        toast.error('Sign in required to update context');
        return;
      }

      const response = await fetch(SUMMARIZE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationIds: selectedContextIds,
          ...(isGeminiOAuthConnectedRef.current && userAISettingsRef.current.useGoogleGemini !== false && geminiOAuthTokenRef.current
            ? { geminiAccessToken: geminiOAuthTokenRef.current }
            : {}),
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Summarization failed: ${errorText}`);
      }
      
      const data = await response.json() as { summary?: string; success?: boolean; error?: string };
      if (!data.summary) {
        toast.message('No context to summarize', {
          description: 'Selected conversations have no messages yet.',
        });
        setSummarizedContext(undefined);
        return;
      }

      setSummarizedContext(data.summary);
      toast.success('Context summarized successfully');
      return data.summary;
    } catch (error) {
      logger.error('Error summarizing context:', { error: error });
      toast.error('Failed to summarize conversations');
      return undefined;
    } finally {
      setIsSummarizing(false);
    }
  }, [selectedContextIds, SUMMARIZE_URL]);

  // Suggest booking a consultation
  const suggestBooking = useCallback(() => {
    setShowBookingSuggestion(true);
    
    const suggestionMsg: UIMessage = {
      role: "assistant",
      id: generateMessageId(),
      parts: [{ 
        type: 'text', 
        content: "It sounds like your project might benefit from a personalized consultation with one of our AI automation experts. Would you like me to help you book a free 30-minute consultation?" 
      }],
    };
    
    tanstackChat.setMessages([...messagesRef.current, suggestionMsg]);
    
    // Save to database
    if (isAuthenticated && user?.id) {
      const extendedMsg = convertUIMessageToExtended(suggestionMsg);
      saveMessageToDb(
        extendedMsg,
        isAuthenticated,
        user.id,
        currentConversationId,
        setCurrentConversationId,
        currentOrgId
      );
    }
  }, [tanstackChat, isAuthenticated, user, currentConversationId, currentOrgId]);

  // Hide booking suggestion
  const hideBookingSuggestion = useCallback(() => {
    setShowBookingSuggestion(false);
  }, []);

  // Clear conversation history
  const clearHistory = useCallback(async () => {
    if (!currentConversationId) {
      startNewConversation();
      return;
    }
    
    try {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('conversation_id', currentConversationId);
      
      if (error) {throw error;}
      
      tanstackChat.setMessages([]);
      setShowWelcomeAnimation(true);
      toast.success('History cleared');
    } catch (error) {
      logger.error('Failed to clear history:', { error: error });
      toast.error('Failed to clear history');
    }
  }, [currentConversationId, tanstackChat, startNewConversation]);

  // Replay a user message — re-sends the same text to the agent
  const handleReplayMessage = useCallback((messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (message?.role === 'user' && message.content) {
      handleSendMessage(message.content);
    }
  }, [messages, handleSendMessage]);

  return {
    // Messages and state
    messages,
    input,
    setInput,
    
    // Loading and error states (mapped from TanStack AI)
    isLoading: tanstackChat.isLoading,
    isLoadingConversation,
    isError: tanstackChat.error !== undefined,
    status: tanstackChat.isLoading ? 'streaming' as const : 'ready' as const,
    error: tanstackChat.error,
    
    // Actions
    handleSendMessage,
    sendMessage: handleSendMessage,
    stop: () => {
      // Intentional stop — don't trigger the dropped-transport recovery.
      intentionalStopRef.current = true;
      activeCortexRunRef.current = null;
      tanstackChat.stop();
    },
    regenerate: tanstackChat.reload,
    resumeStream: undefined as (() => void) | undefined,
    
    // Conversation management
    currentConversationId,
    setCurrentConversationId,
    conversationList,
    loadConversation: loadSession,
    startNewConversation,
    deleteConversation,
    clearHistory,
    toggleContextSelection,
    summarizeSelectedConversations,
    
    // UI state
    showWelcomeAnimation,
    messagesEndRef,
    scrollToBottom,
    
    // Attachments
    pendingAttachments,
    handleFileUploaded,
    
    // Context
    selectedContextIds,
    setSelectedContextIds,
    summarizedContext,
    setSummarizedContext,
    isSummarizing,
    setIsSummarizing,
    
    // Model selection
    selectedModel,
    setSelectedModel,
    selectedProvider,
    setSelectedProvider,
    reasoningEffort,
    setReasoningEffort,
    /** True when an active Google OAuth session is delegating Gemini calls. */
    isGeminiOAuthConnected,
    autoMode,
    setAutoMode,
    
    // Task extraction toggle
    autoExtractTasks,
    setAutoExtractTasks,
    selectedSpecialist,
    setSelectedSpecialist,
    extractTasksFromMessage,
    
    // Helpers
    updateMessageTodoList,
    addAssistantMessage,
    handleReplayMessage,
    
    // Booking suggestions
    showBookingSuggestion,
    suggestBooking,
    hideBookingSuggestion,
    
    // Orchestrator data
    orchestratorData,

    // Shared stream timeline (same reducer the workbench uses)
    liveTimeline,

    // ask_user human-in-the-loop question (202 awaiting_input)
    pendingQuestion,
    answerPendingQuestion,

    // Notebook grounding
    selectedNotebookId,
    setSelectedNotebookId,
  };
};

export type UseChatReturn = ReturnType<typeof useChatImpl>;

export default useChatImpl;
